import { Music2, Play, Pause, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { MusicTrack } from '@/lib/musicTypes';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { useTrackZaps, formatSats } from '@/hooks/useTrackZaps';
import { nip19 } from 'nostr-tools';
import { useMusicPlayer } from '@/contexts/MusicPlayerContext';
import { ZapButton } from './ZapButton';
import { LikeButton } from './LikeButton';
import { ShareButton } from './ShareButton';

interface FeaturedTrackCardProps {
  track: MusicTrack;
  playlist?: MusicTrack[];
  index: number;
}

export function FeaturedTrackCard({ track, playlist, index }: FeaturedTrackCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const author = useAuthor(track.pubkey);
  const artistAuthor = useAuthor(track.artistPubkey || '');
  const { data: zaps } = useTrackZaps(track.event.kind, track.pubkey, track.id);
  const { currentTrack, isPlaying, playTrack } = useMusicPlayer();

  const metadata = author.data?.metadata;
  const uploaderName = metadata?.name || genUserName(track.pubkey);

  // Determine the artist name to display
  const artistMetadata = artistAuthor.data?.metadata;
  const artistDisplayName = track.artistName
    || (track.artistPubkey && (artistMetadata?.name || genUserName(track.artistPubkey)))
    || uploaderName;

  // Calculate total zap amount
  const totalZaps = zaps?.reduce((sum, zap) => sum + zap.amount, 0) || 0;

  // Create naddr for the track
  const naddr = nip19.naddrEncode({
    kind: track.event.kind,
    pubkey: track.pubkey,
    identifier: track.id,
  });

  // Generate gradient background if no image
  const backgroundStyle = track.imageUrl
    ? { backgroundImage: imageLoaded ? `url(${track.imageUrl})` : 'none' }
    : track.gradient
    ? { background: `linear-gradient(135deg, ${track.gradient.join(', ')})` }
    : { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' };

  const isCurrentTrack = currentTrack?.event.id === track.event.id;
  const isCurrentlyPlaying = isCurrentTrack && isPlaying;

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't play if clicking on a link or button
    const target = e.target as HTMLElement;
    if (target.closest('a') || target.closest('button')) {
      return;
    }
    playTrack(track, playlist);
  };

  const handlePlayClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    playTrack(track, playlist);
  };

  return (
    <Card
      className="group overflow-hidden bg-white/[0.06] hover:bg-white/[0.07] active:bg-white/[0.10] md:bg-transparent transition-all cursor-pointer border-0"
      onClick={handleCardClick}
    >
      <CardContent className="p-4">
        {/* Album Art / Gradient - Large */}
        <div className="relative aspect-square mb-4 rounded-lg overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center bg-white/[0.08]"
            style={backgroundStyle}
          >
            {track.imageUrl && (
              <img
                src={track.imageUrl}
                alt={track.title}
                className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-300"
                style={{ opacity: imageLoaded ? 1 : 0 }}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageLoaded(false)}
              />
            )}
            {!track.imageUrl && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <Music2 className="h-20 w-20 text-white/80" />
              </div>
            )}
          </div>

          {/* Action Buttons - Bottom Right */}
          <div className="absolute bottom-3 right-3 z-10 flex gap-2" onClick={(e) => e.stopPropagation()}>
            <LikeButton
              event={track.event}
              size="icon"
              variant="ghost"
              className="bg-white/[0.07] hover:bg-white/[0.12] backdrop-blur-sm h-10 w-10 rounded-full"
            />
            <ShareButton
              track={track}
              size="icon"
              variant="ghost"
              className="bg-white/[0.07] hover:bg-white/[0.12] backdrop-blur-sm h-10 w-10 rounded-full"
            />
            <ZapButton
              event={track.event}
              size="icon"
              variant="ghost"
              className="bg-white/[0.07] hover:bg-white/[0.12] backdrop-blur-sm h-10 w-10 rounded-full"
            />
          </div>

          {/* Play Button Overlay */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
            <Button
              size="icon"
              onClick={handlePlayClick}
              className="h-16 w-16 rounded-full bg-white text-black hover:bg-white/90 hover:scale-110 transition-transform [&_svg]:!text-black"
            >
              {isCurrentlyPlaying ? (
                <Pause className="h-7 w-7" />
              ) : (
                <Play className="h-7 w-7 ml-1" />
              )}
            </Button>
          </div>
        </div>

        {/* Track Info */}
        <div>
          <Link
            to={`/${naddr}`}
            className="block"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-bold text-lg line-clamp-2 hover:text-primary transition-colors">
              {track.title}
            </h3>
          </Link>
          {track.artistPubkey ? (
            <Link
              to={`/${nip19.npubEncode(track.artistPubkey)}`}
              className="text-sm text-muted-foreground line-clamp-1 hover:text-primary transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {artistDisplayName}
            </Link>
          ) : (
            <p className="text-sm text-muted-foreground line-clamp-1">
              {artistDisplayName}
            </p>
          )}

          {/* Zap Total Badge */}
          {totalZaps > 0 && (
            <div className="pt-2">
              <Badge variant="secondary" className="text-xs font-semibold">
                <Zap className="h-3 w-3 mr-1 fill-current" />
                {formatSats(totalZaps)}
              </Badge>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
